import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, MapPin, Calendar, Search } from 'lucide-react';
import archBg from '@/assets/ride-arch-bg.png';
import flexibleCalendar from '@/assets/ride-flexible-calendar.png';
import familyImg from '@/assets/ride-family.png';
import { GAMBIAN_LOCATIONS } from '@/utils/gambianLocations';

type ReservationTab = 'upcoming' | 'past';

const RideRentalsPage = () => {
  const navigate = useNavigate();
  const [pickupLocation, setPickupLocation] = useState('');
  const [showLocationSearch, setShowLocationSearch] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');

  const now = new Date();
  const defaultStart = new Date(now);
  defaultStart.setHours(12, 0, 0, 0);
  const defaultEnd = new Date(now);
  defaultEnd.setDate(defaultEnd.getDate() + 3);
  defaultEnd.setHours(12, 0, 0, 0);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateSelectionStep, setDateSelectionStep] = useState<'start' | 'end'>('start');
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [calendarMonth] = useState(now.getMonth());
  const [calendarYear] = useState(now.getFullYear());

  const [showReservations, setShowReservations] = useState(false);
  const [reservationTab, setReservationTab] = useState<ReservationTab>('upcoming');

  const upcomingReservations = [
    {
      id: 'upcoming-1',
      location: 'Senegambia',
      period: 'Mar 16, 12:00 PM - Mar 18, 12:00 PM',
      car: 'Mercedes C-Class',
      status: 'Confirmed',
    },
  ];

  const pastReservations = [
    {
      id: 'past-1',
      location: 'Banjul',
      period: 'Feb 08, 10:00 AM - Feb 10, 10:00 AM',
      car: 'Toyota Vitz',
      status: 'Completed',
    },
  ];

  const formatDateRange = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fmtTime = (d: Date) => `${d.getHours() % 12 || 12}:${d.getMinutes().toString().padStart(2, '0')} ${d.getHours() >= 12 ? 'PM' : 'AM'}`;
    const s = `${months[startDate.getMonth()]} ${startDate.getDate().toString().padStart(2, '0')}, ${fmtTime(startDate)}`;
    const e = `${months[endDate.getMonth()]} ${endDate.getDate().toString().padStart(2, '0')}, ${fmtTime(endDate)}`;
    return `${s} - ${e}`;
  };

  const locationSuggestions = locationQuery.length >= 2
    ? Object.values(GAMBIAN_LOCATIONS).filter((loc) => loc.name.toLowerCase().includes(locationQuery.toLowerCase())).slice(0, 8)
    : Object.values(GAMBIAN_LOCATIONS).slice(0, 8);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay();

  const isSameDay = (d1: Date | null, d2: Date | null) => {
    if (!d1 || !d2) return false;
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
  };

  const isInRange = (day: number, month: number, year: number) => {
    if (!tempStartDate || !tempEndDate) return false;
    const d = new Date(year, month, day);
    return d > tempStartDate && d < tempEndDate;
  };

  const isBeforeToday = (day: number, month: number, year: number) => {
    const d = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  const handleDayClick = (day: number, month: number, year: number) => {
    if (isBeforeToday(day, month, year)) return;
    const selected = new Date(year, month, day, 12, 0, 0, 0);
    if (dateSelectionStep === 'start') {
      setTempStartDate(selected);
      setTempEndDate(null);
      setDateSelectionStep('end');
    } else if (tempStartDate && selected > tempStartDate) {
      setTempEndDate(selected);
    } else {
      setTempStartDate(selected);
      setTempEndDate(null);
    }
  };

  const confirmDates = () => {
    if (tempStartDate) setStartDate(tempStartDate);
    if (tempEndDate) setEndDate(tempEndDate);
    setShowDatePicker(false);
    setDateSelectionStep('start');
  };

  const renderCalendarMonth = (month: number, year: number) => {
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    const cells: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let i = 1; i <= daysInMonth; i++) cells.push(i);

    return (
      <div className="mb-8">
        <h3 className="text-base font-bold text-gray-900 mb-3">{monthNames[month]} {year}</h3>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((day, i) => {
            if (!day) return <div key={i} />;
            const disabled = isBeforeToday(day, month, year);
            const isStart = isSameDay(tempStartDate, new Date(year, month, day));
            const isEnd = isSameDay(tempEndDate, new Date(year, month, day));
            const inRange = isInRange(day, month, year);

            return (
              <button
                key={i}
                disabled={disabled}
                onClick={() => handleDayClick(day, month, year)}
                className={`h-10 w-full rounded-full text-sm font-medium transition-all ${
                  isStart || isEnd
                    ? 'bg-gray-900 text-white'
                    : inRange
                    ? 'bg-gray-200 text-gray-900'
                    : disabled
                    ? 'text-gray-300'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (showDatePicker) {
    const months = [];
    let m = calendarMonth;
    let y = calendarYear;
    for (let i = 0; i < 3; i++) {
      months.push({ month: m, year: y });
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }

    return (
      <div className="min-h-screen bg-white safe-area-top">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4 border-b border-gray-100">
          <button onClick={() => setShowDatePicker(false)} className="h-10 w-10 flex items-center justify-center">
            <ChevronLeft className="h-5 w-5 text-gray-800" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">Edit Duration</h1>
        </div>

        <div className="grid grid-cols-7 px-4 py-3">
          {dayNames.map((d, i) => (
            <div key={i} className="text-center text-xs font-medium text-gray-400">{d}</div>
          ))}
        </div>

        <div className="px-4 pb-24 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 190px)' }}>
          {months.map(({ month, year }) => renderCalendarMonth(month, year))}
        </div>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 pb-8 pt-4">
          <Button
            onClick={confirmDates}
            disabled={!tempStartDate || !tempEndDate}
            className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl text-base disabled:opacity-40"
          >
            Select dates
          </Button>
        </div>
      </div>
    );
  }

  if (showLocationSearch) {
    return (
      <div className="min-h-screen bg-white safe-area-top">
        <div className="flex items-center gap-3 px-4 pt-12 pb-3 border-b border-gray-100">
          <button onClick={() => setShowLocationSearch(false)} className="h-10 w-10 flex items-center justify-center">
            <ChevronLeft className="h-5 w-5 text-gray-800" />
          </button>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              autoFocus
              type="text"
              value={locationQuery}
              onChange={(e) => setLocationQuery(e.target.value)}
              placeholder="Search for a location"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm border-0 outline-none"
            />
          </div>
        </div>

        <div className="px-4 py-2">
          {locationSuggestions.map((loc, i) => (
            <button
              key={i}
              onClick={() => {
                setPickupLocation(loc.name);
                setShowLocationSearch(false);
                setLocationQuery('');
              }}
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 active:bg-gray-100 rounded-xl"
            >
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-gray-500" />
              </div>
              <span className="text-sm text-gray-800 font-medium">{loc.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white safe-area-top">
      <div className="relative h-64 overflow-hidden">
        <img src={archBg} alt="Gambian Arch" className="w-full h-full object-cover" />
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 pt-12">
          <button onClick={() => navigate('/ride')} className="bg-black/60 rounded-full h-10 w-10 flex items-center justify-center">
            <ChevronLeft className="h-5 w-5 text-white" />
          </button>
          <button onClick={() => setShowReservations(true)} className="bg-gray-900/80 rounded-full px-4 py-2 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-white" />
            <span className="text-sm font-medium text-white">Reservations</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-t-2xl -mt-4 relative z-10 pb-28">
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 text-center mb-6">Rental cars</h2>
        <div className="border-t border-gray-100 mx-6 mb-6" />

        <button
          onClick={() => setShowLocationSearch(true)}
          className="mx-6 mb-3 w-[calc(100%-3rem)] flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5"
        >
          <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <span className={`text-sm truncate ${pickupLocation ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
            {pickupLocation || 'Pickup location'}
          </span>
        </button>

        <button
          onClick={() => {
            setTempStartDate(startDate);
            setTempEndDate(endDate);
            setShowDatePicker(true);
          }}
          className="mx-6 mb-8 w-[calc(100%-3rem)] flex items-center gap-3 bg-gray-100 rounded-xl px-4 py-3.5"
        >
          <Calendar className="w-5 h-5 text-gray-500 flex-shrink-0" />
          <span className="text-sm text-gray-700">{formatDateRange()}</span>
        </button>

        <div className="border-t border-gray-100 mx-6 mb-6" />

        <h3 className="text-lg font-bold text-gray-900 mx-6 mb-4">Why rent with GAMBGO?</h3>

        <div className="flex gap-3 overflow-x-auto px-6 pb-4">
          <div className="flex-shrink-0 w-64 h-64 rounded-2xl overflow-hidden relative">
            <img src={familyImg} alt="Travel just got easier" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-4">
              <h4 className="font-bold text-white mb-1">Travel just got easier</h4>
              <p className="text-xs text-white/90">Rent a car for a weekend getaway or even for a month</p>
            </div>
          </div>

          <div className="flex-shrink-0 w-64 h-64 rounded-2xl overflow-hidden relative">
            <img src={flexibleCalendar} alt="Flexible booking" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-4">
              <h4 className="font-bold text-white mb-1">Flexible booking</h4>
              <p className="text-xs text-white/90">Book a rental car the same day or months in advance</p>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-6 pb-8 pt-4 z-20">
        <Button
          onClick={() =>
            navigate('/ride/rentals/cars', {
              state: {
                pickupLocation,
                startDateISO: startDate.toISOString(),
                endDateISO: endDate.toISOString(),
              },
            })
          }
          disabled={!pickupLocation}
          className="w-full h-12 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-xl text-base disabled:opacity-40"
        >
          Search cars
        </Button>
      </div>

      {showReservations && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-end" onClick={() => setShowReservations(false)}>
          <div className="w-full bg-white rounded-t-2xl p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reservations</h3>
            <div className="grid grid-cols-2 gap-2 bg-gray-100 rounded-xl p-1 mb-4">
              <button
                onClick={() => setReservationTab('upcoming')}
                className={`rounded-lg py-2 text-sm font-semibold ${reservationTab === 'upcoming' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
              >
                Upcoming
              </button>
              <button
                onClick={() => setReservationTab('past')}
                className={`rounded-lg py-2 text-sm font-semibold ${reservationTab === 'past' ? 'bg-white text-gray-900' : 'text-gray-500'}`}
              >
                Past
              </button>
            </div>

            {(reservationTab === 'upcoming' ? upcomingReservations : pastReservations).map((reservation) => (
              <div key={reservation.id} className="border border-gray-200 rounded-xl p-4 mb-3">
                <p className="text-sm font-bold text-gray-900">{reservation.car}</p>
                <p className="text-xs text-gray-600 mt-1">{reservation.location}</p>
                <p className="text-xs text-gray-600 mt-1">{reservation.period}</p>
                <span className="inline-flex mt-2 text-xs font-semibold px-2 py-1 bg-gray-100 rounded-full text-gray-700">{reservation.status}</span>
              </div>
            ))}

            <Button variant="outline" onClick={() => setShowReservations(false)} className="w-full rounded-xl mt-2">
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RideRentalsPage;
